import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RiRefreshLine } from '@remixicon/react'
import { Button } from '@/components/ui/button'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { api } from '@/lib/api'

type ReconcileResult = { added: number; removed: number }

export function PlaylistSpotifySync({ playlistId }: { playlistId: string }) {
	const queryClientHook = useQueryClient()
	const [open, setOpen] = useState(false)
	const [lastResult, setLastResult] = useState<ReconcileResult | null>(null)
	const [error, setError] = useState<string | null>(null)

	const reconcileMutation = useMutation({
		mutationFn: async () => {
			const response = await api.api
				.playlists({ id: playlistId })
				['reconcile-spotify'].post()
			if (response.error) {
				const message =
					typeof response.error.value === 'string'
						? response.error.value
						: (response.error.value as { message?: string })?.message ??
							'Failed to sync with Spotify'
				throw new Error(message)
			}
			return response.data as ReconcileResult
		},
		onSuccess: (result) => {
			setLastResult(result)
			setError(null)
			queryClientHook.invalidateQueries({ queryKey: ['songs', { playlistId }] })
			queryClientHook.invalidateQueries({ queryKey: ['playlists'] })
		},
		onError: (err: Error) => {
			setError(err.message)
			setLastResult(null)
		},
	})

	return (
		<div className="mt-10 border-t border-border/20 pt-8">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-xs font-medium text-muted-foreground/60">Sync with Spotify</p>
					<p className="mt-0.5 text-[11px] text-muted-foreground/30">
						Make this playlist exactly match the connected Spotify playlist — adds missing tracks and removes any extras.
					</p>
				</div>
				<AlertDialog open={open} onOpenChange={setOpen}>
					<AlertDialogTrigger asChild>
						<Button variant="outline" size="sm" disabled={reconcileMutation.isPending}>
							<RiRefreshLine className="size-3.5" />
							{reconcileMutation.isPending ? 'Syncing…' : 'Match Spotify'}
						</Button>
					</AlertDialogTrigger>
					<AlertDialogContent onOverlayClick={() => setOpen(false)}>
						<AlertDialogHeader>
							<AlertDialogTitle>Match Spotify playlist</AlertDialogTitle>
							<AlertDialogDescription>
								This will fetch the connected Spotify playlist and update this one to match exactly.
								Tracks present locally but missing on Spotify will be removed; new tracks will be added.
								If Spotify can't be reached, nothing will be changed.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>Cancel</AlertDialogCancel>
							<AlertDialogAction onClick={() => reconcileMutation.mutate()}>
								Sync now
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
			{lastResult && (
				<p className="mt-3 text-[11px] text-muted-foreground/60">
					Synced — added {lastResult.added}, removed {lastResult.removed}.
				</p>
			)}
			{error && (
				<p className="mt-3 text-[11px] text-destructive/80">
					Sync failed: {error}
				</p>
			)}
		</div>
	)
}
